import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import SplitPane from 'react-split-pane';
import {
  activityItemContent, blockNavigation, isNavigationBlocked, promptNavigation, toggleListSelection
} from 'library/helpers';
import _ from 'lodash';
import {
  addTimelineActivity,
  deleteTimelineActivity,
  deleteTimelineGroup,
  loadRosterBuilderTimeline,
  moveTimelineActivities,
  newRosterBuilderTimeline,
  reorderTimelineGroup,
  resizeTimelineActivities,
  saveRosterBuilderTimeline,
  selectTimelineActivities,
  updateTimelineActivities,
  assignUserToPlaceHolder,
  removeAssignedUserFromPlaceHolder,
  reorderAssignedUser,
  swapUserWithPlaceholder,
  updatePinnedActivities,
  setUsers,
  reorderUsersCyclic
} from 'actions/rosterBuilder.actions';
import { list } from 'actions';
import { connect } from 'react-redux';
import { history } from 'store';
import { TIMELINE_DAYS_TO_SHOW, TIMELINE_START_DATE } from 'selectors/timeline';
import REDUCER_KEY from 'reducers/reducer.keys';

import KeyHandler from 'react-key-handler';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import SlidePanel from 'components/slidePanel/slide-panel';
import {
  Button, Drawer, Icon, message, Modal, Spin, Tabs, Tooltip
} from 'antd';
import {
  GroupRenderer, NotificationsPanel, Sidebar
} from 'components/Timeline';
import { Notifications } from 'views/shared';
import { NavigationPrompt } from 'components/Menu';
import RosterBuilderItemRenderer from 'components/Timeline/RosterBuilderItemRenderer';
import settings from 'settings';
import Timeline from '../../../../libs/timeline/timeline';
import ActivityInput from './activityForm/ActivityInput';
import { defaultSettings, filters, pickers } from './config';
import RosterBuilderUserList from './RosterBuilderUserList';
import WrappedRosterBuilderAddToRosterForm from './RosterBuilderAddToRosterForm';

const { TabPane } = Tabs;

const SLIDE_PANEL_MIN_SIZE = settings.APP_CONFIG.slidePanelVerticalMinSize;
const SLIDE_PANEL_MAX_SIZE = settings.APP_CONFIG.slidePanelVerticalMaxSize;

const DRAG_TYPE = {
  ASSIGN_USER: 'ASSIGN_USER',
  REORDER_USER: 'REORDER_USER'
};

const handleUserListDragStart = (e, user) => {
  e.dataTransfer.setData('data', JSON.stringify({
    type: DRAG_TYPE.ASSIGN_USER,
    user
  }));
};

const handleGroupDragStart = (e, group) => {
  e.dataTransfer.setData('data', JSON.stringify({
    type: DRAG_TYPE.REORDER_USER,
    user: group
  }));
};

const activitySelector = activity => ({
  title: activity.title,
  key: activity.id,
  row: activity.group,
  start: moment.isMoment(activity.start_time) ? activity.start_time : moment(activity.start_time),
  end: moment.isMoment(activity.end_time) ? activity.end_time : moment(activity.end_time),
  color: activity.color,
  activity: activity.activity,
  default_notifications: activity.default_notifications,
  userId: activity.user
});

/* eslint-disable arrow-parens */
const padWithZero = (number) => (number < 10 ? `0${number}` : number.toString());
/* eslint-disable arrow-parens */

const convertMinutesToDurationString = (minutes) => {
  const days = Math.floor(minutes / (24 * 60));
  const remainingMinutes = minutes % (24 * 60);
  const hours = Math.floor(remainingMinutes / 60);
  const mins = remainingMinutes % 60;
  // Exclude the day component if it's zero
  if (days === 0) {
    return `${padWithZero(hours)}:${padWithZero(mins)}:00`;
  }
  return `${days} ${padWithZero(hours)}:${padWithZero(mins)}:00`;
};

const compareFields = (activity, item, groups) => {
  const itemDurationString = convertMinutesToDurationString(item.activity.duration);
  const activityNotifications = JSON.stringify(activity.default_notifications);
  const itemNotifications = JSON.stringify(item.default_notifications);
  const assignedUser = groups.find(group => group.id === activity.placeholder_person_id - 1)?.assignedUser?.uid;

  return activity.activity.id === item.activity.id
    && activity.activity.name === item.activity.name
    && activity.activity.duration === itemDurationString
    && activity.activity.color === item.activity.color
    && activity.activity.active === item.activity.active
    && activity.activity.activity_type.id === item.activity.activity_type.id
    && activity.activity.activity_type.name === item.activity.activity_type.name
    && activity.activity.activity_type.code === item.activity.activity_type.code
    && activityNotifications === itemNotifications
    && activity.user === (assignedUser || null);
};

const { TIMELINE_MODES } = Timeline;
const DEFAULT_TIMELINE_ACTIVITY_CREATE_COUNT = 1;
const DEFAULT_TIMELINE_ACTIVITY_HOURS_BETWEEN = 24;
const DEFAULT_TIMELINE_MAX_DAYS = 7;
const DEFAULT_TIMELINE_SNAP_MINUTES = 15;
const MIN_ZOOM_DAYS = 1;
const MAX_ZOOM_DAYS = TIMELINE_DAYS_TO_SHOW;
const MAX_DAYS = TIMELINE_DAYS_TO_SHOW;

const MOUSE_RIGHT_CLICK = 2;
const DELETE_KEY_CODE = 46;
const BACKSPACE_KEY_CODE = 8;
const CTRL_KEY_CODE = 17;

export const TIMEBAR_FORMAT = {
  majorLabels: {
    minute: {
      short: 'mm', // 01
      long: 'HH:mm' // 12:01
    },
    hour: {
      short: 'HH', // 13
      long: 'HH:mm' // 13:00
    },
    day: {
      short: 'DDD', // 1st
      long: ' [Day] DDD' // Sun, July 3, 2018
    },
    month: {
      short: 'w[+]', // Jan
      long: '[Weeks] w[+]' // January 2018
    },
    year: {
      short: `[${MAX_DAYS}]`,
      long: `[${MAX_DAYS} days]`
    }
  },
  minorLabels: {
    minute: {
      short: 'mm', // 01
      long: 'HH:mm' // 12:01
    },
    hour: {
      short: 'HH', // 13
      long: 'HH:mm' // 13:00
    },
    day: {
      short: 'DDD', // 1
      long: '[Day] DDD' // Sun 1st
    },
    month: {
      short: 'w[+]', // 02
      long: '[Weeks] w[+]' // January
    },
    year: {
      short: '[]', // 2018
      long: '[]' // 2018
    }
  }
};

const mapStateToProps = state => ({
  rosterBuilder: state.rosterBuilderTimeline.rosterBuilder,
  activities: state.api.activities.data.results,
  groups: state.rosterBuilderTimeline.groups,
  items: state.rosterBuilderTimeline.items,
  selected: state.rosterBuilderTimeline.selected,
  inFlight: state.rosterBuilderTimeline.inFlight,
  pinnedActivities: state.rosterBuilderTimeline.rosterBuilderPinnedActivities,
});

const isBitSet = (bit, mask) => (bit & mask) === bit;

const mapDispatchToProps = dispatch => ({
  loadActivityList() {
    dispatch(list(
      REDUCER_KEY.activities.key, {
        page: 0,
        pageSize: 1000,
        filters: [],
        sorted: [{ id: 'name' }]
      }
    ));
  },
  loadUsersList(filtersIn = [], sorted = []) {
    return dispatch(list(
      REDUCER_KEY.users.key, {
        page: 0,
        pageSize: 100000,
        filters: filtersIn,
        sorted
      }
    ));
  },
  dispatchSetUsers(userList) {
    dispatch(setUsers(userList));
  },
  dispatchNewRosterBuilderTimeline() {
    dispatch(newRosterBuilderTimeline());
  },
  dispatchLoadRosterBuilderTimeline(rosterBuilderId) {
    return dispatch(loadRosterBuilderTimeline(rosterBuilderId));
  },
  dispatchSaveRosterBuilderTimeline(rosterBuilderId, activities = [], groups) {
    return dispatch(saveRosterBuilderTimeline(rosterBuilderId, activities, groups));
  },

  dispatchAddTimelineActivity(activity, group, time, createActivityCount, hoursBetween) {
    dispatch(addTimelineActivity(activity, group, time, createActivityCount, hoursBetween));
  },
  dispatchUpdateTimelineActivities(items) {
    dispatch(updateTimelineActivities(items));
  },
  dispatchDeleteTimelineActivity() {
    dispatch(deleteTimelineActivity());
  },

  dispatchSelectTimelineActivities(activityIds = []) {
    dispatch(selectTimelineActivities(activityIds));
  },
  dispatchMoveTimelineActivities(changes, items) {
    dispatch(moveTimelineActivities(changes, items));
  },
  dispatchResizeTimelineActivities(changes, items) {
    dispatch(resizeTimelineActivities(changes, items));
  },

  dispatchReorderTimelineGroup(fromGroupId, toGroupId) {
    dispatch(reorderTimelineGroup(fromGroupId, toGroupId));
  },
  dispatchDeleteTimelineGroup(groupId) {
    dispatch(deleteTimelineGroup(groupId));
  },
  dispatchUpdatePinnedActivities(activities) {
    dispatch(updatePinnedActivities(activities));
  },

  dispatchAssignUserToPlaceHolder(user, placeHolderGroupId) {
    dispatch(assignUserToPlaceHolder(user, placeHolderGroupId));
  },
  dispatchRemoveAssignedUserFromPlaceHolder(placeHolderGroupId) {
    dispatch(removeAssignedUserFromPlaceHolder(placeHolderGroupId));
  },
  dispatchReorderAssignedUser(fromGroupId, toGroupId) {
    dispatch(reorderAssignedUser(fromGroupId, toGroupId));
  },
  dispatchSwapUserWithPlaceholder(fromGroupId, toGroupId) {
    dispatch(swapUserWithPlaceholder(fromGroupId, toGroupId));
  },
  dispatchCycleOrderShiftUsers() {
    dispatch(reorderUsersCyclic());
  },
});

/**
 * RosterBuilder timeline and toolbar
 *
 * @version 1.0.0
 */
class RosterBuilderTimeline extends Component {
  constructor(props) {
    super(props);

    this.MIN_VISIBLE_DATE = moment(TIMELINE_START_DATE);
    this.MAX_VISIBLE_DATE = moment(this.MIN_VISIBLE_DATE)
      .add(TIMELINE_DAYS_TO_SHOW, 'day');
    this.mouseClicks = 0;

    this.state = {
      selectedToolbarItem: null,
      selectedActivity: null,
      selectedActivities: [],

      showToolbar: true,
      showActivityForm: false,
      showBulkActivityForm: false,

      timelineMode: TIMELINE_MODES.SELECT | TIMELINE_MODES.DRAG,

      // This object retains the current timeline option
      // settings selected by the user.
      timelineSettings: {
        ...defaultSettings,
        options: {
          activityCreateCount: DEFAULT_TIMELINE_ACTIVITY_CREATE_COUNT,
          activityHoursBetween: DEFAULT_TIMELINE_ACTIVITY_HOURS_BETWEEN,
          timelineMaxDays: DEFAULT_TIMELINE_MAX_DAYS,
          timelineSnapMinutes: DEFAULT_TIMELINE_SNAP_MINUTES,
          displayTravelLayer: false,
          displayLeaveLayer: false,
          displayAvailabilityLayer: false,
          rosterPivoted: false,
          hideEmptyRows: false
        }
      },

      // This object stores the calculated activity filters
      // to apply to the roster builder timeline.
      customFilters: {
        activity: {
          active: {},
          ids: {},
          item: {}
        }
      },

      visibleTimeStart: this.MIN_VISIBLE_DATE,
      visibleTimeEnd: moment(this.MIN_VISIBLE_DATE)
        .add(DEFAULT_TIMELINE_MAX_DAYS, 'day'),

      isAddToRosterDisabled: false, // TODO
      isAddToRosterModalOpen: false,
      isTimelineSectionExpanded: false,
      isMoved: false
    };

    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.handleSaveRosterBuilder = this.handleSaveRosterBuilder.bind(this);
    this.handleDeleteKeydown = this.handleDeleteKeydown.bind(this);
    this.handleCtrlKeyup = this.handleCtrlKeyup.bind(this);
    this.handleCtrlKeydown = this.handleCtrlKeydown.bind(this);

    this.handleZoomIn = this.handleZoomIn.bind(this);
    this.handleZoomOut = this.handleZoomOut.bind(this);
    this.handlePanBack = this.handlePanBack.bind(this);
    this.handlePanForward = this.handlePanForward.bind(this);

    this.handleSingleMouseClick = this.handleSingleMouseClick.bind(this);

    this.handleItemClicks = this.handleItemClicks.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.handleTimelineInteraction = this.handleTimelineInteraction.bind(this);

    this.handleShowToolbar = this.handleShowToolbar.bind(this);
    this.handleTimelineToolbarItemChange = this.handleTimelineToolbarItemChange.bind(this);
    this.handleCustomFilters = this.handleCustomFilters.bind(this);
    this.handleTimelineToolbarOptionsChange = this.handleTimelineToolbarOptionsChange.bind(this);
    this.handlePinItem = this.handlePinItem.bind(this);

    this.handleGroupDragDrop = this.handleGroupDragDrop.bind(this);
    this.handleGroupDelete = this.handleGroupDelete.bind(this);

    this.handleActivityFormModalOk = this.handleActivityFormModalOk.bind(this);
    this.handleActivityFormModalCancel = this.handleActivityFormModalCancel.bind(this);
    this.filterActivities = this.filterActivities.bind(this);

    this.handleItemContextClick = this.handleItemContextClick.bind(this);
    this.renderGroupTitle = this.renderGroupTitle.bind(this);

    this.handleBulkActivityModalCancel = this.handleBulkActivityModalCancel.bind(this);

    this.unselectActivities = this.unselectActivities.bind(this);
  }

  async componentDidMount() {
    const {
      dispatchSelectTimelineActivities, dispatchNewRosterBuilderTimeline, dispatchLoadRosterBuilderTimeline, match
    } = this.props;

    const { params, router } = match;
    const rosterBuilderId = params.id;
    const isActivityAndUsersFetched = await this.loadRosterBuilderActivityAndUserList();

    if (rosterBuilderId && isActivityAndUsersFetched) {
      dispatchLoadRosterBuilderTimeline(rosterBuilderId)
        .then((res) => {
          if (res.activities.length > 0) {
            this.setState({ isAddToRosterDisabled: false });
          }
          else {
            this.setState({ isAddToRosterDisabled: true });
          }
        })
        .catch((e) => {
          if (e.response && e.response.status === 404) history.push('/roster/rosterBuilders/');
        });
    }
    else {
      dispatchNewRosterBuilderTimeline();
    }

    if (isNavigationBlocked()) {
      history.block((nextLocation) => {
        if (isNavigationBlocked()) {
          promptNavigation(() => {
          },
          () => {
            // Discard changes
            blockNavigation(false);
            router.push(nextLocation);
          });
          return false;
        }
        return true;
      });
    }
    dispatchSelectTimelineActivities();
  }

  componentWillUnmount() {
    const { dispatchNewRosterBuilderTimeline, dispatchSelectTimelineActivities } = this.props;
    blockNavigation(false);
    // Clear redux store for the Roster Builders and selected activities
    dispatchNewRosterBuilderTimeline();
    dispatchSelectTimelineActivities();
  }

  getTimelineContainerRef = (input) => {
    this.timelineContainer = input;
  };

  getActivityFormRef = (form) => {
    this.activityForm = form;
  };

  checkIfAddToRosterButtonDisabled = () => {
    const { items } = this.props;
    if (items.length === 0) {
      this.setState({ isAddToRosterDisabled: true });
    }
  }

  handleCancel = () => {
    this.setState({
      isAddToRosterModalOpen: false
    });
  }

  handleAddToRoster = () => {
    const {
      match, items, groups, rosterBuilder
    } = this.props;
    const { params } = match;
    const rosterBuilderId = params.id;
    const { activities } = rosterBuilder;
    const { isMoved } = this.state;
    const activityIds = activities.map(activity => activity.id);
    const itemIds = items.map(item => item.id);
    // Check if both arrays have the same item IDs
    const sameIds = activityIds.length === itemIds.length && activityIds.every(id => itemIds.includes(id));
    const anyNewItemInItems = items.some(item => item.is_new);
    const commonFieldsMatch = activities.every(activity => {
      const itemVal = items.find(item => item.id === activity.id);
      if (!itemVal) return false;
      return compareFields(activity, itemVal, groups);
    });

    if (!sameIds || anyNewItemInItems) {
      this.handleDispatchSaveRosterBuilder(rosterBuilderId, items, groups, false);
    }
    else {
      /* eslint-disable no-lonely-if */
      if (commonFieldsMatch) {
        if (isMoved) {
          this.handleDispatchSaveRosterBuilder(rosterBuilderId, items, groups, false);
        }
        else {
          this.setState({ isAddToRosterModalOpen: true });
        }
      }
      else {
        this.handleDispatchSaveRosterBuilder(rosterBuilderId, items, groups, false);
      }
      /* eslint-disable no-lonely-if */
    }
  }

  handlePushUsers = () => {
    const { dispatchCycleOrderShiftUsers } = this.props;
    dispatchCycleOrderShiftUsers();
  }

  handleDispatchSaveRosterBuilder(rosterBuilderId, items, groups, isCalledDuringSave) {
    const {
      dispatchSaveRosterBuilderTimeline
    } = this.props;
    dispatchSaveRosterBuilderTimeline(rosterBuilderId, items, groups)
    .then(
      async () => {
        blockNavigation(false);
        await message.success('Save Successful.');
        if (isCalledDuringSave) {
          if (items.length > 0) {
            this.setState({
              isAddToRosterDisabled: false,
            });
          }
          else {
            this.setState({
              isAddToRosterDisabled: true,
            });
          }
        }
        else {
          this.setState({ isAddToRosterModalOpen: true, isMoved: false });
        }
      },
      async () => {
        await message.error('Save Failed.');
        if (isCalledDuringSave) {
          this.setState({
            isAddToRosterDisabled: true,
          });
        }
        else {
          this.setState({ isAddToRosterModalOpen: true });
        }
      }
    );
  }

  saveRosterBuilder() {
    const {
      match, items, groups
    } = this.props;
    const { params } = match;
    const rosterBuilderId = params.id;
    this.handleDispatchSaveRosterBuilder(rosterBuilderId, items, groups, true);
  }

  filterActivities(data) {
    const { timelineSettings } = this.state;
        const { timeFilterStart, timeFilterEnd } = timelineSettings.options;
        const timeFilterStartMoment = timeFilterStart instanceof moment ? timeFilterStart : null; // if not a valid format ignore
        const timeFilterEndMoment = timeFilterEnd instanceof moment ? timeFilterEnd : null; // if not a valid format ignore
        const { customFilters } = this.state;

        const { item, ids, active = {} } = customFilters.activity;

        const activityItem = item.searchValue || '';
        const activityIds = ids.searchValue || [];

        let matched = true;

        for (const activityFilter of activityItem) {
          matched = true;
          matched = matched && data.title.toLowerCase() === activityFilter.label.toLowerCase();

          // if the activity match one of multiple activity filter then break
          if (matched) {
            break;
          }
        }

        if (activityIds.length) {
          matched = matched && activityIds.includes(data.activity.id);
        }

        if (active.searchValue) {
          matched = matched && data.activity.active;
        }

        if (timeFilterStartMoment) {
          // If defined should remove assignments that start < given time
          const filterHrs = timeFilterStartMoment.hour();
          const filterMins = timeFilterStartMoment.minute();
          const activityHrs = data.start_time.hour();
          const activityMins = data.end_time.minute();
          if (activityHrs < filterHrs) {
            return false;
          }
          if (activityHrs === filterHrs && activityMins < filterMins) {
            return false;
          }
        }

        if (timeFilterEndMoment) {
          // If defined should remove assignments that end > given time
          const filterHrs = timeFilterEndMoment.hour();
          const filterMins = timeFilterEndMoment.minute();
          const activityHrs = data.start_time.hour();
          const activityMins = data.end_time.minute();

          if (activityHrs > filterHrs) {
            return false;
          }
          if (activityHrs === filterHrs && activityMins > filterMins) {
            return false;
          }
        }

        return matched;
  }

  handleActivityFormModalOk(id, item) {
    const { activities, dispatchUpdateTimelineActivities } = this.props;
    blockNavigation(true);

    const activity = activities.find(a => a.id === item.activity.key);

    dispatchUpdateTimelineActivities([{
      ...item,
      activity
    }]);
    message.success('Assignment updated');
  }

  handleActivityFormModalCancel() {
    this.setState({
      showActivityForm: false,
      selectedActivity: null
    });
  }

  handleBulkActivityModalCancel() {
    this.setState({
      showBulkActivityForm: false,
      selectedActivities: []
    });
  }

  handleItemContextClick(event) {
    const { key } = event;

    // Show the Edit Activity dialog for the selected item
    const { items, selected } = this.props;
    const activity = _.clone(items.find(item => item.id === event.item.props.data.key), true);

    const selectedActivities = items.filter(item => selected.includes(item.id));
    if (activity) {
      switch (key) {
      case 'edit':
        this.setState({
          showActivityForm: true,
          selectedActivity: activity
        });
        break;
      case 'editselected':
        if (selectedActivities.filter(item => item.is_new).length) {
          message.warn('Assignments must be saved before bulk editing');
        }
        else {
          this.setState({
            showBulkActivityForm: true,
            selectedActivities: selected
          });
        }
        break;
      default:
        break;
      }
    }
  }

  handleZoomIn() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffMinutes = Math.max(visibleTimeEnd.diff(visibleTimeStart, 'minutes') / 2, MIN_ZOOM_DAYS * 24 * 60);
    const diffDays = Math.round(((diffMinutes / 60) / 24) * 100) / 100;

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes'),
      timelineSettings: {
        ...prevState.timelineSettings,
        options: {
          ...prevState.timelineSettings.options,
          timelineMaxDays: diffDays
        }
      }
    }));
  }

  handleZoomOut() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffMinutes = Math.min(2 * visibleTimeEnd.diff(visibleTimeStart, 'minutes'), MAX_ZOOM_DAYS * 24 * 60);
    const diffDays = Math.round(((diffMinutes / 60) / 24) * 100) / 100;

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes'),
      timelineSettings: {
        ...prevState.timelineSettings,
        options: {
          ...prevState.timelineSettings.options,
          timelineMaxDays: diffDays
        }
      }
    }));
  }

  handlePanForward() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const endDate = moment.min(moment(visibleTimeEnd)
      .add(diffDays, 'days'), this.MAX_VISIBLE_DATE);

    dispatchSelectTimelineActivities();
    this.setState({
      visibleTimeStart: moment(endDate)
        .add(-diffDays, 'days'),
      visibleTimeEnd: endDate
    });
  }

  handlePanBack() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const startDate = moment.max(moment(visibleTimeStart)
      .add(-diffDays, 'days'), this.MIN_VISIBLE_DATE);

    dispatchSelectTimelineActivities();
    this.setState(() => ({
      visibleTimeStart: startDate,
      visibleTimeEnd: moment(startDate)
        .add(diffDays, 'days')
    }));
  }

  async handleDeleteKeydown() {
    blockNavigation(true);
    const { dispatchDeleteTimelineActivity, selected, rosterBuilder } = this.props;
    const { activities } = rosterBuilder;
    await dispatchDeleteTimelineActivity();
    const activityIds = activities.map(item => item.id);
    const allActivitiesSelected = activityIds.every(id => selected.includes(id));
    if (allActivitiesSelected) {
      this.setState({ isAddToRosterDisabled: true });
    }
  }

  handleCtrlKeydown() {
    const { timelineMode } = this.state;
    const resizeEnabled = isBitSet(Timeline.TIMELINE_MODES.RESIZE, timelineMode);

    if (!resizeEnabled) {
      let newTimelineMode = timelineMode;
      newTimelineMode &= ~TIMELINE_MODES.DRAG; // Turn off drag
      newTimelineMode |= TIMELINE_MODES.RESIZE; // Turn on resize
      this.setState({ timelineMode: newTimelineMode });
    }
  }

  handleCtrlKeyup() {
    const { timelineMode } = this.state;
    let newTimelineMode = timelineMode;
    newTimelineMode |= TIMELINE_MODES.DRAG; // Turn on drag
    newTimelineMode &= ~TIMELINE_MODES.RESIZE; // Turn off resize
    this.setState({ timelineMode: newTimelineMode });
  }

  handleMouseWheel(e) {
    const { dispatchSelectTimelineActivities } = this.props;

    if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
      dispatchSelectTimelineActivities();
      const offsetTop = this.timelineContainer.scrollTop;
      this.timelineContainer.scrollTop = offsetTop + e.deltaY;
    }
  }

  handleSingleMouseClick(e, key) {
    // Update the user selection
    const { dispatchSelectTimelineActivities, selected } = this.props;
    const activityList = toggleListSelection(selected, key);

    dispatchSelectTimelineActivities(activityList);
  }

  handleItemClicks(e, key) {
    if (e.button !== MOUSE_RIGHT_CLICK) { // if user clicked anything other than the right
      this.handleSingleMouseClick(e, key);
    }
  }

  handleRowClick() {
    const { dispatchSelectTimelineActivities } = this.props;
    dispatchSelectTimelineActivities();
  }

  handleRowDoubleClick(e, rowNumber, clickedTime, snappedClickedTime) {
    const { dispatchAddTimelineActivity } = this.props;
    const { selectedToolbarItem, timelineSettings } = this.state;
    const { options } = timelineSettings;

    if (selectedToolbarItem) {
      blockNavigation(true);
      dispatchAddTimelineActivity(
        selectedToolbarItem,
        rowNumber,
        snappedClickedTime,
        options.activityCreateCount,
        options.activityHoursBetween
      );
    }
  }

  handleGroupDragDrop(e, group) {
    const data = JSON.parse(e.dataTransfer.getData('data'));
    const { type, user } = data;

    switch (type) {
    case DRAG_TYPE.ASSIGN_USER:
      this.assignUser(group, user);
      break;

    case DRAG_TYPE.REORDER_USER:
      this.reorderUser(group, user);
      break;

    default:
      break;
    }
  }

  loadRosterBuilderActivityAndUserList() {
    const { dispatchSetUsers, loadActivityList, loadUsersList } = this.props;
    // Load the activity and user groups for the timeline then load the user assignments.
    return Promise.all([loadActivityList(), loadUsersList()])
      .then(async (values) => {
          const userList = values[1].results;
          await dispatchSetUsers(userList);
          return true;
      }).catch(() => false);
  }

  assignUser(group, user) {
    const { dispatchAssignUserToPlaceHolder } = this.props;

    const newUser = {
      id: group.id,
      uid: user.id,
      title: `${user.first_name} ${user.last_name}`,
      active: user.is_active
    };
    dispatchAssignUserToPlaceHolder(newUser, group.id);
  }

  reorderUser(group, user) {
    const { dispatchReorderAssignedUser, dispatchSwapUserWithPlaceholder } = this.props;

    // If both the dragStart and dragEnd users are assigned then perform a reorder.
    // Otherwise a placeholder user is involved and we merely trade places
    //
    if (!!group.assignedUser && !!user.assignedUser) {
      dispatchReorderAssignedUser(user.id, group.id);
    }
    else {
      dispatchSwapUserWithPlaceholder(user.id, group.id);
    }
  }

  handleGroupDelete(group) {
    const { dispatchRemoveAssignedUserFromPlaceHolder } = this.props;
    dispatchRemoveAssignedUserFromPlaceHolder(group.id);
  }

  handleCustomFilters(customFilters) {
    this.setState(prevState => ({
      customFilters: {
        ...prevState.customFilters,
        ...customFilters
      }
    }));
  }

  handleTimelineInteraction(type, changes, items) {
    const { dispatchSelectTimelineActivities, dispatchMoveTimelineActivities, dispatchResizeTimelineActivities } = this.props;
    const { selected } = this.props;

    switch (type) {
    case Timeline.changeTypes.dragStart:
      return selected;

    case Timeline.changeTypes.dragEnd:
      blockNavigation(true);
      this.setState({ isMoved: true });
      dispatchMoveTimelineActivities(changes, items);
      break;

    case Timeline.changeTypes.resizeStart:
      return selected;

    case Timeline.changeTypes.resizeEnd:
      blockNavigation(true);
      dispatchResizeTimelineActivities(changes, items);
      break;

    case Timeline.changeTypes.itemsSelected: {
      const activities = changes.map(item => item.key);
      dispatchSelectTimelineActivities(activities);
      break;
    }

    case Timeline.changeTypes.snappedMouseMove: {
      break;
    }

    default:
      return changes;
    }
    return changes;
  }

  handleShowToolbar() {
    this.setState(prevState => ({
      showToolbar: !prevState.showToolbar,
      isTimelineSectionExpanded: !prevState.isTimelineSectionExpanded
    }));
  }

  handleTimelineToolbarItemChange(item) {
    const itemType = (item ? item.type : '');
    if (itemType === 'activity') {
      this.setState({ selectedToolbarItem: item.data });
    }
  }

  handleTimelineToolbarOptionsChange(options) {
    this.setState(prevState => ({
      timelineSettings: {
        ...prevState.timelineSettings,
        options
      },
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(options.timelineMaxDays, 'day')
    }));
  }

  /**
   * Updates the list of pinned items in roster timeline store.
   * @param items New list of items to be saved in store.
   * @param itemType Identifier for `items`, e.g. 'activity'
   */
  handlePinItem(items, itemType) {
    const { dispatchUpdatePinnedActivities } = this.props;

    switch (itemType) {
      case 'activity': {
        dispatchUpdatePinnedActivities(items);
        break;
      }
      default:
        break;
    }
  }

  handleSaveRosterBuilder() {
    this.saveRosterBuilder();
  }

  unselectActivities(id) {
    const { selectedActivities } = this.state;
    this.setState({
      selectedActivities: selectedActivities.filter(i => i !== id)
    });
  }

  renderGroupTitle() {
    const { groups, rosterBuilder } = this.props;
    const { activities } = rosterBuilder;
    const title = `${groups.length - 1} User${groups.length === 1 ? '' : 's'}`;
    const isCyclicShiftButtonDisabled = !(activities?.length > 0);
    return (
      <div
        className="bhp-group-title"
        style={{
          width: '100%', margin: 'auto', display: 'flex', justifyContent: 'space-between', position: 'relative', alignItems: 'baseline', padding: '0 15px 0 85px'
        }}
      >
        <span>{title}</span>
        <Tooltip title="Shift Users" placement="bottom">
          <Button className="refreshButton" shape="circle" size="small" icon="sync" onClick={this.handlePushUsers} disabled={isCyclicShiftButtonDisabled} />
        </Tooltip>
      </div>
    );
  }

  renderToolbar(showToolbar, isLoading) {
    const { permissions } = this.props;
    const { isAddToRosterDisabled } = this.state;
    return (
      <ToolBar>
        <ToolBar.Right>
          <Tooltip title="Zoom out" placement="bottom">
            <Button size="small" icon="left" loading={isLoading} onClick={this.handlePanBack}>
              Pan backward
            </Button>
          </Tooltip>
          <Tooltip title="Zoom in" placement="bottom">
            <Button size="small" icon="right" loading={isLoading} onClick={this.handlePanForward}>
              Pan forwards
            </Button>
          </Tooltip>
          <Tooltip title="Zoom out" placement="bottom">
            <Button size="small" icon="minus" loading={isLoading} onClick={this.handleZoomOut}>
              Zoom out
            </Button>
          </Tooltip>
          <Tooltip title="Zoom in" placement="bottom">
            <Button size="small" icon="plus" loading={isLoading} onClick={this.handleZoomIn}>
              Zoom in
            </Button>
          </Tooltip>
          {permissions.CanAdminActivities()
            .hasPermissions() && (
            <Tooltip title="Save" placement="bottom">
              <Button className="saveButton" disabled={false} type="primary" size="small" icon="save" loading={isLoading} onClick={this.handleSaveRosterBuilder}>
                Save
              </Button>
            </Tooltip>
          )}
          {permissions.CanAdminActivities()
            .hasPermissions() && (
            <Tooltip title="Add to Roster" placement="bottom">
              <Button className="addToRosterButton" type="primary" size="small" icon="save" loading={isLoading} disabled={isAddToRosterDisabled} onClick={this.handleAddToRoster}>
                Add to Roster
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Show Options" placement="bottom">
            <Button className="showToolbar" size="small" icon={showToolbar ? 'menu-unfold' : 'menu-fold'} onClick={this.handleShowToolbar}>
              {showToolbar ? 'Hide Options' : 'Show Options'}
            </Button>
          </Tooltip>
        </ToolBar.Right>
      </ToolBar>
    );
  }

  render() {
    const {
      selectedActivities, showBulkActivityForm, showActivityForm, showToolbar, timelineSettings, visibleTimeStart, visibleTimeEnd, timelineMode, selectedActivity, isTimelineSectionExpanded, isAddToRosterModalOpen
    } = this.state;
    const {
      dispatchLoadRosterBuilderTimeline, match, groups, items, rosterBuilder, inFlight, selected, permissions, pinnedActivities
    } = this.props;
    const newUserId = Math.max(...groups.map(g => g.id));
    const rosterBuilderId = match.params.id;
    // Show the following toolbar options
    const timelineToolbarTabs = {
      showActivities: permissions.CanAdminActivities()
        .hasPermissions(),
      showTemplates: false,
      showFilters: true,
      showFiltersToolbar: false,
      showOptions: permissions.CanAdminActivities()
        .hasPermissions(),
      showOptionsToolbar: false,
      optionFields: {
        showDisplayTravelLayer: false,
        showDisplayLeaveLayer: false,
        showDisplayAvailabilityLayer: false,
        showRosterPivot: false,
        showHideEmptyRows: false,
        showAssignmentTitleFormat: false,
        showActivityCreateCount: true,
        showActivityHoursBetween: true,
        showTimelineMaxDays: true,
        showTimelineSnapMinutes: true,
        showTimeFilter: false,
        showHideUnallocatedRows: false
      }
    };
     // Apply filter to the timeline activities
     const filteredItems = items.filter(this.filterActivities);
     const timelineItems = filteredItems.map(i => activitySelector(i));
     const selectedItems = items.filter(item => selectedActivities.includes(item.id))
       .map(i => activitySelector(i));

    // group for eact items you have
    return (
      <div className="bhp-rosterBuilder-timeline bhp-container">
        <KeyHandler keyEventName="keydown" keyCode={DELETE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keydown" keyCode={BACKSPACE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keyup" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeyup} />
        <KeyHandler keyEventName="keydown" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeydown} />
        <HeaderBar title={`Roster Builder: ${rosterBuilder.name}`} toolbar={this.renderToolbar(showToolbar, inFlight)} />
        <SlidePanel direction="right" width={365} show={showToolbar}>
          <Sidebar
            tabs={timelineToolbarTabs}
            defaultSettings={timelineSettings}
            options={timelineSettings.options}
            onItemChange={this.handleTimelineToolbarItemChange}
            onFilterChange={this.handleCustomFilters}
            onOptionsChange={this.handleTimelineToolbarOptionsChange}
            onPinItem={this.handlePinItem}
            pinnedActivities={pinnedActivities}
            config={{
              pickers,
              filters: {
                ...filters,
                showUserFilter: false
              }
            }}
            visibleTimeStart={null}
            visibleTimeEnd={null}
          />
          <SplitPane split="vertical" defaultSize="19%" minSize={SLIDE_PANEL_MIN_SIZE} maxSize={SLIDE_PANEL_MAX_SIZE} style={!isTimelineSectionExpanded ? { marginRight: '365px' } : { marginRight: '0px' }}>
            <RosterBuilderUserList className="bhp-manageusers" onDragStart={handleUserListDragStart} />
            <Spin spinning={inFlight} indicator={<Icon type="loading" spin />} tip="Loading...">
              <div ref={this.getTimelineContainerRef} className="bhp-timeline" onWheel={this.handleMouseWheel}>
                <Timeline
                  shallowUpdateCheck
                  showCursorTime
                  isLeftComponentPresent
                  leftComponentId="ExtendedTable-SelectUser"
                  cursorTimeFormat="[Day] DDD - HH:mm"
                  groups={groups}
                  groupTitleRenderer={this.renderGroupTitle}
                  groupRenderer={({ group }) => {
                    const rowIndex = group.id + 1;
                    const title = `${group.title} ${group.assignedUser ? `- ${group.assignedUser.title}` : rowIndex}`;
                    return (
                      <GroupRenderer
                        isUser
                        rowIndex={rowIndex.toString()}
                        title={title}
                        data={{ ...group }}
                        isClickable={false}
                        isDraggable
                        showDelete={group.id !== newUserId && permissions.CanAdminActivities()
                          .hasPermissions()}
                        onDragStart={handleGroupDragStart}
                        onDragDrop={this.handleGroupDragDrop}
                        onDelete={this.handleGroupDelete}
                        canHover={false}
                      />
                    );
                  }}
                  items={timelineItems}
                  itemRenderer={({ ...props }) => (
                    <RosterBuilderItemRenderer
                      {...props}
                      hiddenContextItems={[...(selected.length > 1 ? ['edit'] : []), 'reassign', 'action', ...(selected.length > 1 ? [] : ['editselected'])]}
                      onContextClick={this.handleItemContextClick}
                    />
                  )}
                  selectedItems={selected}
                  timelineMode={permissions.CanAdminActivities()
                    .hasPermissions() ? timelineMode : 0}
                  timebarFormat={TIMEBAR_FORMAT}
                  startDate={visibleTimeStart}
                  endDate={visibleTimeEnd}
                  snapMinutes={Number(timelineSettings.options.timelineSnapMinutes)}
                  groupOffset={250}
                  onItemClick={this.handleItemClicks}
                  onRowClick={this.handleRowClick}
                  onRowDoubleClick={this.handleRowDoubleClick}
                  onInteraction={this.handleTimelineInteraction}
                  componentId="rbmap"
                />
              </div>
            </Spin>
          </SplitPane>

        </SlidePanel>

        {selectedActivity && (
          <Drawer
            placement="right"
            width={1200}
            closable
            maskClosable
            visible={showActivityForm}
            destroyOnClose
            onClose={this.handleActivityFormModalCancel}
            title="Edit Activity"
          >
            <Tabs
              defaultActiveKey="1"
            >
              <TabPane tab="Detail" key="1">
                <ActivityInput
                  canAdmin
                  isEditMode
                  ref={this.getActivityFormRef}
                  dataSource={selectedActivity}
                  title="Activity"
                  picker="time"
                  showNotes={false}
                  showTags={false}
                  onSaveClick={this.handleActivityFormModalOk}
                  onCancelClick={this.handleActivityFormModalCancel}
                  themeName="activity"
                />
              </TabPane>
              <TabPane tab="Notifications" key="2" disabled={selectedActivity && selectedActivity.is_new}>
                <Notifications
                  onDelete={() => dispatchLoadRosterBuilderTimeline(rosterBuilderId)}
                  onDeleteCallback={(response, callback) => {
                    callback();
                  }}
                  onSave={() => dispatchLoadRosterBuilderTimeline(rosterBuilderId)}
                  themeName="activitytemplate"
                  objectId={selectedActivity.id}
                  permissions={permissions}
                />
              </TabPane>
            </Tabs>
          </Drawer>
        )}
        <Drawer
          placement="right"
          width={1200}
          closable
          maskClosable
          visible={showBulkActivityForm}
          destroyOnClose
          onClose={this.handleBulkActivityModalCancel}
          title="Edit Bulk Activities"
        >
          <Tabs
            defaultActiveKey="1"
          >
            <TabPane tab="Notifications" key="1">
              <NotificationsPanel
                headerTitle="Selected Activities"
                itemRenderer={activityItemContent}
                items={items.map(item => ({
                  ...item,
                  notifications: item.default_notifications
                }))}
                onCancel={this.handleBulkActivityModalCancel}
                onDelete={() => dispatchLoadRosterBuilderTimeline(rosterBuilderId)}
                onDeselect={this.unselectActivities}
                onSave={() => dispatchLoadRosterBuilderTimeline(rosterBuilderId)}
                reducer={REDUCER_KEY.notificationRule}
                removeText="Remove all notifications from selected activities"
                selectedObjects={selectedActivities}
                selectedItems={selectedItems}
                themeName="activitytemplate"
                title="Notification"
              />
            </TabPane>
          </Tabs>
        </Drawer>
        <NavigationPrompt when={isNavigationBlocked()}>
          {(isOpen, onConfirm, onCancel) => (
            <Modal
              visible={isOpen}
              onOk={onCancel}
              onCancel={onConfirm}
              title="Unsaved Changes"
              okText="No - Remain here"
              cancelText="Yes - Discard Changes"
              maskClosable={false}
              closable={false}
            >
              <div>
                <p>
                  You have made changes to the timeline that have not been saved.
                </p>
                <p>
                  Do you wish to discard these changes?
                </p>
              </div>
            </Modal>
          )}
        </NavigationPrompt>

        <WrappedRosterBuilderAddToRosterForm
          isAddToRosterModalOpen={isAddToRosterModalOpen}
          rosterBuilder={rosterBuilder}
          onCancel={this.handleCancel}
        />
      </div>
    );
  }
}

RosterBuilderTimeline.propTypes = {
  permissions: PropTypes.object.isRequired,
  rosterBuilder: PropTypes.object,
  activities: PropTypes.array,
  groups: PropTypes.array,
  items: PropTypes.array,
  inFlight: PropTypes.bool,
  match: PropTypes.object,
  router: PropTypes.object,
  route: PropTypes.object,
  selected: PropTypes.array.isRequired,
  pinnedActivities: PropTypes.array,

  dispatchNewRosterBuilderTimeline: PropTypes.func,
  dispatchLoadRosterBuilderTimeline: PropTypes.func,
  dispatchSaveRosterBuilderTimeline: PropTypes.func,

  dispatchAddTimelineActivity: PropTypes.func,
  dispatchUpdateTimelineActivities: PropTypes.func,
  dispatchDeleteTimelineActivity: PropTypes.func,

  dispatchSelectTimelineActivities: PropTypes.func,
  dispatchMoveTimelineActivities: PropTypes.func,
  dispatchResizeTimelineActivities: PropTypes.func,

  dispatchReorderTimelineGroup: PropTypes.func,
  dispatchDeleteTimelineGroup: PropTypes.func,
  loadActivityList: PropTypes.func,
  loadUsersList: PropTypes.func,
  loadRosterBuilderActivityAndUserList: PropTypes.func,
  dispatchUpdatePinnedActivities: PropTypes.func.isRequired,

  dispatchSetUsers: PropTypes.func,
  dispatchAssignUserToPlaceHolder: PropTypes.func.isRequired,
  dispatchReorderAssignedUser: PropTypes.func.isRequired,
  dispatchSwapUserWithPlaceholder: PropTypes.func.isRequired,
  dispatchRemoveAssignedUserFromPlaceHolder: PropTypes.func.isRequired,
  dispatchCycleOrderShiftUsers: PropTypes.func.isRequired,
};

RosterBuilderTimeline.defaultProps = {
  rosterBuilder: {},
  groups: [],
  activities: [],
  items: [],
  inFlight: false,
  match: PropTypes.shape({
    params: PropTypes.object,
    router: PropTypes.object,
    route: PropTypes.string
  }).isRequired,
  pinnedActivities: [],
  router: {},
  route: {},

  dispatchNewRosterBuilderTimeline() {
  },
  dispatchLoadRosterBuilderTimeline() {
  },
  dispatchSaveRosterBuilderTimeline() {
  },

  dispatchAddTimelineActivity() {
  },
  dispatchUpdateTimelineActivities() {
  },
  dispatchDeleteTimelineActivity() {
  },

  dispatchSelectTimelineActivities() {
  },
  dispatchMoveTimelineActivities() {
  },
  dispatchResizeTimelineActivities() {
  },

  dispatchReorderTimelineGroup() {
  },
  dispatchDeleteTimelineGroup() {
  },
  dispatchSetUsers() {
  },

  loadActivityList() {
  },
  loadUsersList() {
  },
  loadRosterBuilderActivityAndUserList() {},

};

export default connect(mapStateToProps, mapDispatchToProps, null,
  { withRef: true })(RosterBuilderTimeline);
